import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Star,
  Sparkles,
  Truck,
  ShieldCheck,
  Package,
  ChevronRight,
  Users,
  Flag,
  Fuel,
} from 'lucide-react';
import { resolveLogoUrl } from '../components/insured/useNTOABranding';
import { useSiteContent } from '../hooks/useSiteContent';
import { useSEO } from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Session 259.10 — DEFAULTS mirror the current Zion NTOA palette so
// first-time visitors don't see a flash of a generic blue/amber theme
// before the branding fetch lands. If the tenant ever rebrands, either
// (a) update these constants alongside the branding change, or
// (b) rely on the ``ntoa_branding_cache`` localStorage entry below —
// returning visitors hydrate from that instantly and never flash.
const DEFAULTS = {
  logo_url: '/ntoa-logo.png',
  primary_color: '#1e3a8a',
  accent_color: '#f5c20a',
  hero_gradient_from: '#0d0d0d',
  hero_gradient_to: '#e71313',
  tagline: 'National Transportation Operators Association',
};

const BRANDING_CACHE_KEY = 'ntoa_branding_cache_v1';

/** Read a previously-fetched branding payload out of localStorage.
 *  Cheap synchronous hydrate — kills the FOUC for repeat visitors even
 *  if the DEFAULTS above drift from the live palette. */
function readCachedBranding() {
  try {
    const raw = typeof window !== 'undefined' && window.localStorage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic sanity — must at least look like a branding object.
    if (parsed && typeof parsed === 'object' && parsed.primary_color) return parsed;
  } catch {
    /* corrupted entry — ignore */
  }
  return null;
}

/**
 * NTOAPublicLanding — public marketing page for the National
 * Transportation Operators Association (ntoassoc.com / .net).
 *
 * No auth required. The "Member Portal" CTA jumps visitors straight to
 * /insured/login, which is also the NTOA member sign-in.
 *
 * Branding (logo, colors, tagline) is fetched from the public NTOA
 * branding endpoint so changes from the Super Admin → NTOA → Branding
 * panel reflect immediately on this page without a deploy.
 */
export default function NTOAPublicLanding() {
  // Session 215.82 — GSC-friendly per-route SEO with dedicated
  // NTOA Organization JSON-LD so Google can serve /ntoa as its own
  // entity, distinct from Zion Underwriting Partners.
  useSEO({
    title:
      'NTOA — National Transportation Operators Association | Member Benefits, Discounts & Advocacy for Owner-Operators',
    description:
      'Join the National Transportation Operators Association (NTOA) — trucking industry membership for owner-operators, small fleets, and their families. Preferred insurance rates, DOT compliance help, IFTA/IRP support, licensed swag, and nationwide advocacy.',
    path: '/ntoa',
    image: `${process.env.REACT_APP_SITE_URL || 'https://zionuw.com'}/ntoa-logo.png`,
    jsonLdId: 'ntoa',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://zionuw.com/ntoa#organization',
          'name': 'National Transportation Operators Association',
          'alternateName': 'NTOA',
          'url': 'https://zionuw.com/ntoa',
          'logo': 'https://zionuw.com/ntoa-logo.png',
          'description':
            'Membership association for owner-operators, fleets, and transportation professionals — negotiated discounts, DOT/IFTA/IRP compliance help, insurance access, and industry advocacy.',
          'areaServed': 'US',
          'knowsAbout': [
            'Owner-operator support',
            'DOT compliance',
            'IFTA fuel tax reporting',
            'IRP apportioned registration',
            'Commercial trucking insurance',
            'Fleet safety',
          ],
        },
        {
          '@type': 'WebPage',
          'url': 'https://zionuw.com/ntoa',
          'name': 'National Transportation Operators Association',
          'isPartOf': { '@id': 'https://zionuw.com/#website' },
          'about': { '@id': 'https://zionuw.com/ntoa#organization' },
        },
      ],
    },
  });
  // Session 259.10 — Synchronous localStorage hydrate on mount kills the
  // color-flash for anyone who's landed on /ntoa before. First-timers get
  // the DEFAULTS above (which mirror the current live palette).
  const [branding, setBranding] = useState(() => ({ ...DEFAULTS, ...(readCachedBranding() || {}) }));
  const [stats, setStats] = useState({ partners_count: 0, swag_count: 0 });
  const [promo, setPromo] = useState({
    active: false,
    mode: null,
    cta_label: 'Sign up free for a year',
    discount_percent: 0,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API}/public/ntoa/landing`, {
          credentials: 'omit',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        const fresh = { ...DEFAULTS, ...(data.branding || {}) };
        setBranding(fresh);
        // Persist for next visit so the FOUC never comes back even if
        // the palette drifts.
        try {
          window.localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(fresh));
        } catch {
          /* private mode / quota — ignore */
        }
        setStats({
          partners_count: data.partners_count || 0,
          swag_count: data.swag_count || 0,
        });
        setPromo(data.promo || promo);
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const logo = resolveLogoUrl(branding.logo_url);
  const primary = branding.primary_color || DEFAULTS.primary_color;
  const accent = branding.accent_color || DEFAULTS.accent_color;
  const heroFrom = branding.hero_gradient_from || DEFAULTS.hero_gradient_from;
  const heroTo = branding.hero_gradient_to || DEFAULTS.hero_gradient_to;

  // Session 213.78 — operator-editable hero copy via CMS.
  // Session 216.1 — Each of the 6 "Member benefit" cards is now
  // CMS-editable. Set `benefit_N_enabled` to `"false"` (or empty
  // string) to hide any card that doesn't reflect a partnership
  // you have yet — turn it back on later by setting to `"true"`.
  // The icon stays hardcoded in JSX so keep card position stable.
  const cms = useSiteContent('ntoa_landing', {
    hero_title_lead: 'Built on Service.',
    hero_title_highlight: 'Driven by Purpose.',
    hero_subtitle: 'The National Transportation Operators Association is the home for owner-operators, fleets, and the families behind the wheel. Negotiated discounts, advocacy, and a community that has your back on every mile.',
    join_cta_label: 'Join NTOA',

    benefits_heading:  'Save thousands. Run smarter.',
    benefits_subtitle: 'Every dollar a member doesn\'t spend on overhead is a dollar back in the truck — or back to their family. Here\'s some of what comes with membership:',

    // Card 1 — Fuel & truck-stop discounts
    benefit_1_enabled: 'true',
    benefit_1_title:   'Negotiated fuel & truck-stop discounts',
    benefit_1_body:    'Member rates at participating fuel stops, tire shops, repair facilities, and parts suppliers across the country.',
    // Card 2 — Insurance & compliance
    benefit_2_enabled: 'true',
    benefit_2_title:   'Insurance & compliance',
    benefit_2_body:    'Direct access to Zion Underwriting Partners for trucking coverage, plus member rates on DOT compliance, IFTA, and IRP services.',
    // Card 3 — Equipment & maintenance
    benefit_3_enabled: 'true',
    benefit_3_title:   'Equipment & maintenance',
    benefit_3_body:    'Group buying power on parts, tools, telematics, and fleet maintenance services through NTOA partners.',
    // Card 4 — Tax prep & accounting
    benefit_4_enabled: 'true',
    benefit_4_title:   'Tax prep & accounting',
    benefit_4_body:    'Discounted IFTA/IRP filings, 1099 reporting, and bookkeeping for owner-operators and small fleets.',
    // Card 5 — Exclusive NTOA merchandise
    benefit_5_enabled: 'true',
    benefit_5_title:   'Exclusive NTOA merchandise',
    benefit_5_body:    'Member-only apparel, hats, and gear. Wear the colors. Tell the world who you ride with.',
    // Card 6 — Community & advocacy
    benefit_6_enabled: 'true',
    benefit_6_title:   'Community & advocacy',
    benefit_6_body:    'Member-only updates on regs, rate trends, and industry news. A voice for operators where decisions are made.',

    // Session 216.2 — footer sub-line (external website URL). Hide
    // the ntoassoc.com line here until the domain goes live.
    footer_website_enabled: 'true',
    footer_website_label:   'ntoassoc.com',
  });

  // Helper — treats "false" / "" / "0" / "no" as OFF, everything
  // else as ON. Keeps the CMS admin UX intuitive.
  const cmsEnabled = (v) => {
    if (v === undefined || v === null) return true;
    const s = String(v).trim().toLowerCase();
    return !(s === '' || s === 'false' || s === '0' || s === 'no' || s === 'off');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ntoa-public-landing">
      {/* ─── Top nav ─── */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md border-b"
        style={{ backgroundColor: 'rgba(15,23,42,0.92)', borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="NTOA"
              className="h-10 w-10 object-contain"
              data-testid="ntoa-public-nav-logo"
            />
            <div className="leading-tight">
              <div className="text-white font-bold text-lg tracking-tight">NTOA</div>
              <div
                className="text-[10px] uppercase tracking-widest font-semibold"
                style={{ color: accent }}
              >
                Members Association
              </div>
            </div>
          </div>
          <nav className="flex items-center gap-2 md:gap-3">
            <a
              href="#benefits"
              className="hidden md:inline text-sm text-slate-200 hover:text-white px-3 py-2 rounded-md transition-colors"
              data-testid="ntoa-public-nav-benefits"
            >
              Benefits
            </a>
            <a
              href="#about"
              className="hidden md:inline text-sm text-slate-200 hover:text-white px-3 py-2 rounded-md transition-colors"
              data-testid="ntoa-public-nav-about"
            >
              Who We Are
            </a>
            <a
              href="#partners"
              className="hidden md:inline text-sm text-slate-200 hover:text-white px-3 py-2 rounded-md transition-colors"
              data-testid="ntoa-public-nav-partners"
            >
              Partners
            </a>
            <Link
              to="/insured/login"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 px-3 md:px-4 py-2 rounded-md shadow-sm transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: accent }}
              data-testid="ntoa-public-nav-member-portal"
            >
              Member Portal
              <ChevronRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* ─── Hero ─── */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          background: `linear-gradient(135deg, ${heroFrom} 0%, ${primary} 55%, ${heroTo} 100%)`,
        }}
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-5"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: accent,
                border: `1px solid ${accent}40`,
              }}
              data-testid="ntoa-public-hero-eyebrow"
            >
              <Flag className="h-3.5 w-3.5" />
              {branding.tagline || DEFAULTS.tagline}
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight"
              data-testid="ntoa-public-hero-title"
            >
              {cms.hero_title_lead}
              <br />
              <span style={{ color: accent }}>{cms.hero_title_highlight}</span>
            </h1>
            <p
              className="mt-5 text-base md:text-lg text-slate-200 max-w-xl leading-relaxed"
              data-testid="ntoa-public-hero-sub"
            >
              {cms.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/insured/register" data-testid="ntoa-public-hero-cta-portal">
                <Button
                  size="lg"
                  className="font-semibold shadow-lg text-slate-900 w-full sm:w-auto"
                  style={{ backgroundColor: accent }}
                >
                  <Star className="h-4 w-4 mr-2 fill-current" />
                  Join Today
                </Button>
              </Link>
              <a href="#benefits" data-testid="ntoa-public-hero-cta-benefits">
                <Button
                  size="lg"
                  variant="outline"
                  className="font-semibold w-full sm:w-auto text-white border-white/30 hover:bg-white/10 hover:text-white bg-transparent"
                >
                  Explore Member Benefits
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </a>
            </div>
            {promo.active && promo.mode === 'percent_off' && promo.discount_percent > 0 && (
              <div
                className="mt-6 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold"
                style={{
                  backgroundColor: `${accent}22`,
                  color: accent,
                  border: `1px solid ${accent}66`,
                }}
                data-testid="ntoa-public-promo-banner"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Limited-time promo — {promo.discount_percent}% off all paid plans.
              </div>
            )}
          </div>
          <div className="hidden md:flex items-center justify-center">
            <div
              className="rounded-full p-14"
              style={{
                background: 'radial-gradient(circle, rgba(255,255,255,0.10) 0%, transparent 70%)',
              }}
            >
              <img
                src={logo}
                alt="NTOA — National Transportation Operators Association shield"
                className="h-80 w-80 md:h-96 md:w-96 object-contain drop-shadow-2xl"
                style={{ backgroundColor: 'transparent' }}
                data-testid="ntoa-public-hero-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Stat strip ─── */}
      <section
        className="border-y bg-white"
        style={{ borderColor: '#e2e8f0' }}
        data-testid="ntoa-public-stats"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat
            icon={Users}
            value="Nationwide"
            label="Operator community"
            color={primary}
          />
          <Stat
            icon={Sparkles}
            value={stats.partners_count > 0 ? `${stats.partners_count}+` : '—'}
            label="Partner discounts"
            color={accent}
          />
          <Stat
            icon={Package}
            value={stats.swag_count > 0 ? `${stats.swag_count}` : '—'}
            label="NTOA merchandise items"
            color={primary}
          />
          <Stat
            icon={ShieldCheck}
            value="24/7"
            label="Member portal access"
            color={accent}
          />
        </div>
      </section>

      {/* ─── Who we are ─── */}
      <section
        id="about"
        className="py-20 md:py-24 bg-slate-50"
        data-testid="ntoa-public-about"
      >
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div
            className="inline-block text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: primary }}
          >
            Who we are
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-6">
            A community where the road comes first.
          </h2>
          <div className="grid md:grid-cols-2 gap-8 text-slate-700 leading-relaxed">
            <p>
              NTOA was founded to give independent operators and small fleets a
              seat at the table. Insurance, fuel, parts, compliance, lodging —
              every cost of doing business adds up faster than the miles.
              Members earn back time and dollars through negotiated rates we
              couldn't get alone.
            </p>
            <p>
              We're built on the same values our members are: show up, work
              hard, take care of the people next to you. We exist to make the
              business side of trucking simpler — so members can focus on what
              they do best.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Member benefits ─── */}
      <section
        id="benefits"
        className="py-20 md:py-24 border-t"
        style={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }}
        data-testid="ntoa-public-benefits"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div
            className="inline-block text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: accent }}
          >
            Member benefits
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3">
            {cms.benefits_heading}
          </h2>
          <p className="text-slate-300 max-w-2xl mb-12">
            {cms.benefits_subtitle}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cmsEnabled(cms.benefit_1_enabled) && (
              <BenefitCard
                icon={Fuel}
                accent={accent}
                title={cms.benefit_1_title}
                body={cms.benefit_1_body}
              />
            )}
            {cmsEnabled(cms.benefit_2_enabled) && (
              <BenefitCard
                icon={ShieldCheck}
                accent={accent}
                title={cms.benefit_2_title}
                body={cms.benefit_2_body}
              />
            )}
            {cmsEnabled(cms.benefit_3_enabled) && (
              <BenefitCard
                icon={Truck}
                accent={accent}
                title={cms.benefit_3_title}
                body={cms.benefit_3_body}
              />
            )}
            {cmsEnabled(cms.benefit_4_enabled) && (
              <BenefitCard
                icon={Star}
                accent={accent}
                title={cms.benefit_4_title}
                body={cms.benefit_4_body}
              />
            )}
            {cmsEnabled(cms.benefit_5_enabled) && (
              <BenefitCard
                icon={Package}
                accent={accent}
                title={cms.benefit_5_title}
                body={cms.benefit_5_body}
              />
            )}
            {cmsEnabled(cms.benefit_6_enabled) && (
              <BenefitCard
                icon={Users}
                accent={accent}
                title={cms.benefit_6_title}
                body={cms.benefit_6_body}
              />
            )}
          </div>
        </div>
      </section>

      {/* ─── Partners teaser ─── */}
      <section
        id="partners"
        className="py-20 md:py-24 bg-white border-t border-slate-200"
        data-testid="ntoa-public-partners"
      >
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <div
            className="inline-block text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: primary }}
          >
            Partners
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
            {stats.partners_count > 0
              ? `${stats.partners_count}+ partner discounts waiting for you.`
              : 'A growing list of partners. All for our members.'}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            Discount codes, member rates, and partner perks are gated behind the
            Member Portal so we can keep them exclusive to active NTOA members.
            Sign in to see what's available today.
          </p>
          <Link to="/insured/login" data-testid="ntoa-public-partners-cta">
            <Button
              size="lg"
              className="font-semibold shadow-md text-slate-900"
              style={{ backgroundColor: accent }}
            >
              <Star className="h-4 w-4 mr-2 fill-current" />
              Sign in to see partner discounts
            </Button>
          </Link>
        </div>
      </section>

      {/* ─── Join CTA ─── */}
      <section
        className="py-20 md:py-24 text-white"
        style={{
          background: `linear-gradient(135deg, ${primary} 0%, ${heroTo} 100%)`,
        }}
        data-testid="ntoa-public-join-cta"
      >
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4">
            Ready to join the association?
          </h2>
          <p className="text-slate-100 max-w-2xl mx-auto mb-8 leading-relaxed">
            Create your NTOA account in seconds to browse partner discounts
            and gear. Activate your membership from inside the portal any
            time to unlock redemption.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/insured/register" data-testid="ntoa-public-cta-join-now">
              <Button
                size="lg"
                className="font-semibold shadow-lg text-slate-900 w-full sm:w-auto"
                style={{ backgroundColor: accent }}
              >
                {promo.active && promo.mode === 'free'
                  ? promo.cta_label || 'Join today'
                  : 'Join Today'}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/insured/login" data-testid="ntoa-public-cta-existing">
              <Button
                size="lg"
                variant="outline"
                className="font-semibold w-full sm:w-auto text-white border-white/40 hover:bg-white/10 hover:text-white bg-transparent"
              >
                Existing member sign-in
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer
        className="text-slate-400 text-sm"
        style={{ backgroundColor: '#0b1220' }}
        data-testid="ntoa-public-footer"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid md:grid-cols-[2fr_1fr_1fr] gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="NTOA" className="h-10 w-10 object-contain" />
              <div className="text-white font-bold text-lg">NTOA</div>
            </div>
            <p className="text-xs text-slate-400 max-w-md leading-relaxed">
              National Transportation Operators Association — a members
              association serving owner-operators, fleets, and the trucking
              community. Built on Service. Driven by Purpose.
            </p>
          </div>
          <div>
            <div
              className="text-xs uppercase tracking-widest font-bold mb-3"
              style={{ color: accent }}
            >
              Members
            </div>
            <ul className="space-y-2 text-xs">
              <li>
                <Link
                  to="/insured/login"
                  className="hover:text-white transition-colors"
                  data-testid="ntoa-public-footer-portal"
                >
                  Member Portal
                </Link>
              </li>
              <li>
                <a href="#benefits" className="hover:text-white transition-colors">
                  Benefits
                </a>
              </li>
              <li>
                <a href="#partners" className="hover:text-white transition-colors">
                  Partners
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div
              className="text-xs uppercase tracking-widest font-bold mb-3"
              style={{ color: accent }}
            >
              Legal
            </div>
            <ul className="space-y-2 text-xs">
              <li>
                <Link
                  to="/privacy-policy"
                  className="hover:text-white transition-colors"
                  data-testid="ntoa-public-footer-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service"
                  className="hover:text-white transition-colors"
                  data-testid="ntoa-public-footer-terms"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div
          className="border-t text-xs text-slate-500 py-5"
          style={{ borderColor: '#1e293b' }}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-2">
            <div>© {new Date().getFullYear()} National Transportation Operators Association. All rights reserved.</div>
            {cmsEnabled(cms.footer_website_enabled) && (
              <div data-testid="ntoa-public-footer-website">{cms.footer_website_label}</div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="h-12 w-12 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${color}1a`, color }}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
        {value}
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </div>
    </div>
  );
}

function BenefitCard({ icon: Icon, accent, title, body }) {
  return (
    <div
      className="rounded-xl p-6 border transition-all hover:-translate-y-1"
      style={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderColor: 'rgba(255,255,255,0.08)',
      }}
    >
      <div
        className="h-11 w-11 rounded-lg flex items-center justify-center mb-4"
        style={{ backgroundColor: `${accent}22`, color: accent }}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-bold text-white text-base mb-2">{title}</div>
      <div className="text-sm text-slate-300 leading-relaxed">{body}</div>
    </div>
  );
}
